
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useImageCache } from '@/hooks/use-image-cache';
import { useTranslation } from 'react-i18next';
import type { GameItem } from '@/data/gameData';

interface GameCardProps {
  item: GameItem;
  isSelected: boolean;
  isMatched: boolean;
  onClick: () => void;
}

const GameCard = ({ item, isSelected, isMatched, onClick }: GameCardProps) => {
  const { t } = useTranslation();
  const { fetchImage, isLoading } = useImageCache();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  // Les cartes révélées sont soit sélectionnées soit validées
  const isRevealed = isSelected || isMatched;

  useEffect(() => {
    if (item.searchQuery && isRevealed && !imageUrl && !imageError) {
      fetchImage(item.searchQuery).then((url) => {
        if (url) {
          setImageUrl(url);
        } else {
          setImageError(true);
        }
      });
    }
  }, [item.searchQuery, isRevealed, imageUrl, imageError, fetchImage]);

  const handleImageError = () => {
    setImageError(true);
    setImageUrl(null);
  };

  // Get translated name using the item id as the translation key
  const translatedName = t(`game.items.${item.id}`, { defaultValue: item.name });

  return (
    <Card 
      className={`
        aspect-square cursor-pointer transition-all duration-300 transform hover:scale-105
        ${isSelected && !isMatched ? 'bg-gradient-to-br from-blue-100 to-purple-100' : ''}
        ${isMatched ? 'bg-gradient-to-br from-green-50 to-emerald-100 ring-2 ring-green-400 opacity-90' : ''}
        ${!isRevealed ? 'bg-gradient-to-br from-gray-200 to-gray-300' : ''}
      `}
      onClick={isMatched ? undefined : onClick}
    >
      <CardContent className="p-2 h-full flex flex-col items-center justify-center">
        {isRevealed ? (
          <div className="text-center h-full flex flex-col items-center justify-center">
            {imageUrl && !imageError ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <img 
                  src={imageUrl} 
                  alt={translatedName}
                  className="w-full h-3/4 object-cover rounded mb-1"
                  onError={handleImageError}
                />
                <div className="text-xs font-medium text-gray-700 leading-tight text-center">
                  {translatedName}
                </div>
              </div>
            ) : (
              <>
                <div className="text-2xl mb-1">
                  {isLoading(item.searchQuery || '') ? '⏳' : item.emoji}
                </div>
                <div className="text-xs font-medium text-gray-700 leading-tight">
                  {translatedName}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="text-2xl text-gray-400">
            🎯
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GameCard;
